import { useState } from 'react'
import { EyeOff, Eye } from 'lucide-react'

interface ContentWarningProps {
    text: string
    children: React.ReactNode
}

/**
 * Content-scope warning: replaces text + embeds with a clickable bar.
 * Author row, timestamps, and action bar remain visible outside this wrapper.
 */
export function ContentWarning({ text, children }: ContentWarningProps) {
    const [revealed, setRevealed] = useState(false)

    if (!revealed) {
        return (
            <button
                type="button"
                aria-label={`Show content warning: ${text}`}
                onClick={e => {
                    e.stopPropagation()
                    setRevealed(true)
                }}
                className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark text-text-secondary text-sm hover:bg-border-light/20 dark:hover:bg-border-dark/20 transition-colors"
            >
                <EyeOff className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 text-left truncate">{text}</span>
                <span className="flex-shrink-0 text-xs font-medium text-primary">Show</span>
            </button>
        )
    }

    return (
        <div>
            <button
                type="button"
                onClick={e => {
                    e.stopPropagation()
                    setRevealed(false)
                }}
                className="mt-1 mb-1 flex items-center gap-1.5 text-text-secondary text-xs hover:text-text-primary transition-colors"
            >
                <Eye className="w-3.5 h-3.5" />
                <span>{text}</span>
                <span className="text-primary font-medium">Hide</span>
            </button>
            {children}
        </div>
    )
}

interface MediaWarningOverlayProps {
    text: string
    children: React.ReactNode
}

/**
 * Media-scope warning: blurs images/video behind a clickable overlay.
 * Post text remains visible.
 */
export function MediaWarningOverlay({ text, children }: MediaWarningOverlayProps) {
    const [revealed, setRevealed] = useState(false)

    if (revealed) {
        return (
            <div>
                <button
                    type="button"
                    aria-label={`Hide (${text})`}
                    onClick={e => {
                        e.stopPropagation()
                        setRevealed(false)
                    }}
                    className="mb-1 flex items-center gap-1.5 text-text-secondary text-xs hover:text-text-primary transition-colors"
                >
                    <Eye className="w-3.5 h-3.5" aria-hidden="true" />
                    <span className="text-primary font-medium">Hide</span>
                    <span className="text-text-secondary">({text})</span>
                </button>
                {children}
            </div>
        )
    }

    return (
        <div className="relative rounded-lg overflow-hidden">
            <div className="blur-xl pointer-events-none select-none" aria-hidden="true">
                {children}
            </div>
            <button
                type="button"
                onClick={e => {
                    e.stopPropagation()
                    setRevealed(true)
                }}
                aria-label={`Show sensitive content: ${text}`}
                className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 dark:bg-black/60"
            >
                <EyeOff className="w-6 h-6 text-white mb-1.5" aria-hidden="true" />
                <span className="text-white text-sm font-medium" aria-hidden="true">
                    {text}
                </span>
                <span className="text-white/80 text-xs mt-0.5" aria-hidden="true">
                    Tap to show
                </span>
            </button>
        </div>
    )
}
